/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useLayoutEffect, useState } from 'react';
import { HStack, Box, Image, useDisclosure } from 'chakra-v3';
import type { PlatformTextComponent } from '@karya/core';
import type { ComponentRendererProps } from '../../shared/Types';
import { getTypedParams } from '../../shared/Types';
import { Text } from '../../../../../components/Text';
import { useNext, UseNextStrategy } from '../../../hooks/useNext';
import { Button } from '../../../../../components/buttons/Button';
import { withBaseUrl } from '@/utils/url';

export const PlatformText: React.FC<ComponentRendererProps> = ({ node, controller, isCurrent }) => {
  const params = getTypedParams<PlatformTextComponent>(node);
  const text = params.text ?? '';
  // TODO: Implement MANUAL strategy which waits for user interaction before moving to next component.
  const strategy = (params.type ?? 'AUTO') as UseNextStrategy;
  const delay = params.delay;
  const collapsable = params.collapsable ?? true;
  const maxLines = params.max_lines ?? 3;
  const textRef = React.useRef<HTMLDivElement>(null);

  const [textLineCount, setTextLineCount] = useState(0);

  useLayoutEffect(() => {
    if (!textRef.current) {
      return;
    }

    const element = textRef.current;
    const measure = () => {
      const computedStyles = window.getComputedStyle(element);
      const lineHeight = parseFloat(computedStyles.lineHeight);
      if (Number.isNaN(lineHeight) || lineHeight <= 0) return;
      const lines = Math.ceil(element.scrollHeight / lineHeight);
      setTextLineCount(lines);
    };

    measure();

    // Setup a ResizeObserver to re-measure if the element's size changes (e.g., due to window resizing)
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  // Manage collapsible state using Chakra UI's useDisclosure hook
  const collapsibleState = useDisclosure();

  // Enable read more toggle if maxLines is set or collapsable is explicitly enabled
  const shouldShowToggle = collapsable && textLineCount > maxLines;
  const shouldClampText = shouldShowToggle && !collapsibleState.open;

  // Handle next component navigation strategies (AUTO, DELAY, MANUAL)
  useNext({
    // HACK: Treat MANUAL as AUTO to allow auto progression, since manual progression
    // is not currently supported by this component.
    // TODO: Implement proper manual progression handling in the future.
    strategy: strategy === 'MANUAL' ? 'AUTO' : strategy,
    delay,
    controller,
    isCurrent,
  });

  const logo = (
    <Box
      w="24px"
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      alignSelf="flex-start"
      pt="2px"
    >
      <Image
        src={withBaseUrl('/KaryaLogo.svg')}
        alt="Karya Logo"
        w="24px"
        h="24px"
        objectFit="contain"
        filter="brightness(0) invert(1)"
      />
    </Box>
  );

  return (
    <Box py="10px" display="flex" flexDirection="column" gap="12px">
      <HStack gap="12px" alignItems="flex-start">
        {logo}
        <Box flex="1" display="flex" flexDirection="column" gap="8px">
          <Text
            ref={textRef}
            color="text.primary"
            wordBreak="break-all"
            lineClamp={shouldClampText ? maxLines : undefined}
          >
            {text}
          </Text>

          {shouldShowToggle && (
            <Button variant="outline" w="fit-content" p="0" h="auto" minH="auto" onClick={collapsibleState.onToggle}>
              <HStack gap="4px" alignItems="center">
                <Text variant="bodyBold" color="brand.secondaryBlue2">
                  {collapsibleState.open ? 'Show Less' : 'Show More'}
                </Text>
              </HStack>
            </Button>
          )}
        </Box>
      </HStack>
    </Box>
  );
};
